import { useState, useEffect } from 'react';
import { X, Download, RefreshCw, Share, Plus } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Detectar si ya está instalada como PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true; // iOS específico
    if (isStandalone) {
      setIsInstalled(true);
      // NO hacer return aquí - necesitamos registrar el listener de actualizaciones
    }

    // Detectar si es iOS/Safari
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);

    if (isIOSDevice && !isStandalone) {
      setIsIOS(true);

      // Verificar si el usuario descartó el prompt en las últimas 72 horas (más tiempo para iOS)
      const dismissedAt = localStorage.getItem('pwa-ios-prompt-dismissed');
      if (dismissedAt) {
        const hoursSinceDismissal =
          (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
        if (hoursSinceDismissal < 72) {
          return; // No mostrar el prompt aún
        }
      }

      // Mostrar instrucciones de iOS después de 5 segundos
      setTimeout(() => {
        setShowIOSInstructions(true);
      }, 5000);
    }

    // Capturar el evento beforeinstallprompt (solo si NO está instalada)
    const handleBeforeInstallPrompt = e => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Verificar si el usuario descartó el prompt en las últimas 24 horas
      const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
      if (dismissedAt) {
        const hoursSinceDismissal =
          (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
        if (hoursSinceDismissal < 24) {
          return; // No mostrar el prompt aún (pero el listener sigue activo)
        }
      }

      // Esperar 3 segundos antes de mostrar el prompt (mejor UX)
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000);
    };

    // Detectar actualizaciones del service worker (SIEMPRE activo, incluso en modo standalone)
    const handleSWUpdate = event => {
      // Guardar referencia al waiting worker para envicarle mensaje después
      if (event.detail && event.detail.registration) {
        setWaitingWorker(event.detail.registration.waiting);
      }
      setShowUpdatePrompt(true);
    };

    // Solo registrar beforeinstallprompt si NO está instalada
    if (!isStandalone) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }

    // SIEMPRE registrar el listener de actualizaciones
    window.addEventListener('sw-update-available', handleSWUpdate);

    return () => {
      if (!isStandalone) {
        window.removeEventListener(
          'beforeinstallprompt',
          handleBeforeInstallPrompt
        );
      }
      window.removeEventListener('sw-update-available', handleSWUpdate);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ Usuario aceptó instalar la PWA');
      setIsInstalled(true);
    } else {
      console.log('❌ Usuario rechazó instalar la PWA');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Volver a mostrar en 24 horas
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  const handleIOSDismiss = () => {
    setShowIOSInstructions(false);
    // Volver a mostrar en 72 horas para iOS
    localStorage.setItem('pwa-ios-prompt-dismissed', Date.now().toString());
  };

  const handleUpdate = () => {
    // Enviar mensaje al waiting service worker (no al controller)
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      // Fallback: enviar al controller (menos ideal pero mejor que nada)
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }

    // Recargar la página para aplicar la actualización
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Si está instalada, solo mostrar banner de actualizaciones
  if (isInstalled && !showUpdatePrompt) return null;

  // Instrucciones para iOS (modal completo)
  if (showIOSInstructions && isIOS) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/50 animate-fade-in">
        <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-md max-w-md w-full p-6 animate-slide-up">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="page-title text-gray-900">Instalar Nova ERP</h3>
              <p className="text-sm text-gray-600 mt-1">
                Accede más rápido desde tu iPhone
              </p>
            </div>
            <button
              onClick={handleIOSDismiss}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Instrucciones paso a paso */}
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                1
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Toca el botón <strong>Compartir</strong>
                </p>
                <div className="mt-2 flex items-center gap-2 text-blue-600">
                  <Share className="w-5 h-5" />
                  <span className="text-xs">
                    (Ubicado en la parte inferior de Safari)
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                2
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Selecciona{' '}
                  <strong>&quot;Agregar a pantalla de inicio&quot;</strong>
                </p>
                <div className="mt-2 flex items-center gap-2 text-green-600">
                  <Plus className="w-5 h-5" />
                  <span className="text-xs">Busca esta opción en el menú</span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                3
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Confirma tocando <strong>&quot;Agregar&quot;</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  ¡Listo! La app aparecerá en tu pantalla de inicio
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500">
              Instalando la app tendrás acceso más rápido y podrás usarla sin
              conexión
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Banner de actualización disponible
  if (showUpdatePrompt) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md animate-slide-down">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <RefreshCw className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm sm:text-base font-medium truncate">
                Nueva versión disponible
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleUpdate}
                className="px-4 py-1.5 bg-white text-blue-600 rounded-lg font-semibold text-sm hover:bg-blue-50 transition-colors whitespace-nowrap"
              >
                Actualizar ahora
              </button>
              <button
                onClick={() => setShowUpdatePrompt(false)}
                className="p-1 hover:bg-blue-600 rounded-lg transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Banner de instalación
  if (showInstallPrompt) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-[#6B9080] to-[#5a7a6b] text-white shadow-md animate-slide-down">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Download className="w-5 h-5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-semibold">
                  Instalar Nova ERP
                </p>
                <p className="text-xs sm:text-sm opacity-90 hidden sm:block">
                  Accede más rápido desde tu pantalla de inicio
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleInstallClick}
                className="px-4 sm:px-6 py-1.5 sm:py-2 bg-white text-[#6B9080] rounded-lg font-semibold text-sm sm:text-base hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg"
              >
                Instalar
              </button>
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-[#5a7a6b] rounded-lg transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
